import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatShort, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveRefresh } from "@/lib/live";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BarChart, ComposedChart, Bar, Line, LabelList, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Info } from "lucide-react";

const ORANGE = "#FF5A00";
const ROSE = "#E11D48";
const SLATE = "#52525B";  // "Jobs Got" (orders received) bar — a different population from Jobs Printed, kept visually distinct
const AMBER = "#D97706";  // the part of "Printed" that was received in an earlier period — backlog cleared, not fresh work
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TOOLTIP_STYLE = { borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 };

const pad = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthBounds = (y, m) => [`${y}-${pad(m + 1)}-01`, isoDay(new Date(y, m + 1, 0))];
const yearsBack = () => { const y = new Date().getFullYear(); return [y, y - 1, y - 2, y - 3, y - 4].map(String); };
// Plain numbers, not "K"/"L" short-form — that read as confusing here (good/waste metres, m/hour etc.).
const num = (v, digits = 1) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: digits }));
const metres = (v, digits = 1) => (v == null ? "—" : `${num(v, digits)} m`);
// Hours read naturally as a plain count ("1,854 hrs"), not abbreviated like metres ("1.8K") — and anything
// under an hour is clearer in minutes than as "0.3".
const hrs = (v) => {
  if (v == null) return "—";
  if (v > 0 && v < 1) return `${Math.round(v * 60)} min`;
  return `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: v < 100 ? 1 : 0 })} hrs`;
};
const pct = (v) => (v == null ? "—" : `${v}%`);

// What each figure counts, shown on the card (hover) and in the list under the cards.
const HELP = {
  jobs: "Jobs printed in this period, counted the way the client's production logs count them: a job is in every month it had a logged run, once however many runs it had. Before the logs begin it is counted on its printing date in the Medpack tracking sheet. A job split into lots counts once.",
  jobsDay: "Jobs printed divided by the days that had printing.",
  jobsShift: "Jobs printed divided by the shifts that had printing (A 06-14, B 14-22, C 22-06, from the start time).",
  good: "Good running metres off the press: the partial counter of each print run, i.e. the total counter less in-process waste. Make-ready and waste are not in it.",
  rm: "Metres used on the press (make-ready + total counter) against the metres issued from stores for the same jobs. The rest went back to stores, or its return was not recorded. Over 100% means more was used than issued.",
  efficiency: "Good metres / (good + wasted metres) — the share of everything run through the press that came out usable. The full OEE formula also weighs in machine downtime and rated capacity, logged separately in Quick Entry; until that is used, this figure is effectively the quality/wastage rate.",
  goodAvg: "Good running metres divided by the days, shifts or run hours that had printing.",
  waste: "Make-ready + in-process waste metres from the Printing records (plus setup and LSD metres from Quick Entry), divided by the days, shifts or run hours that had printing.",
};

const fyStart = () => { const d = new Date(); return `${d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1}-04-01`; };

const tick = (key, group) => (group === "year" ? key : group === "month" ? `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(2, 4)}` : `${key.slice(8)}/${key.slice(5, 7)}`);

/** Value above each bar (SVG text, so nothing wraps on narrow bars). */
const valueLabel = (rows, field, color, size = 10) => function ValueLabel({ x, y, width, index }) {
  const row = rows[index];
  if (!row || !row[field]) return null;
  return <text x={x + width / 2} y={y - 5} textAnchor="middle" fontSize={size} fontWeight={600} fill={color} className="font-mono-num">{formatShort(row[field], 1)}</text>;
};

/** A second figure inside the bar, near the top (bold number + small caption), white-on-colour — same look as
 * the Sales chart's "invs" badge. Skipped on a bar too short to hold two lines without spilling out the top. */
const insideLabel = (rows, field, caption, minHeight = 34, fmt = (v) => Number(v).toLocaleString("en-IN")) => function InsideLabel({ x, y, width, height, index }) {
  const row = rows[index];
  if (!row || !row[field] || height < minHeight) return null;
  const cx = x + width / 2;
  return (
    <g className="font-mono-num">
      <text x={cx} y={y + 17} textAnchor="middle" fontSize={12} fontWeight={700} fill="#fff">{fmt(row[field])}</text>
      <text x={cx} y={y + 29} textAnchor="middle" fontSize={8} fill="#fff" fillOpacity={0.85}>{caption}</text>
    </g>
  );
};

export default function ProductionAnalytics() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = isoDay(new Date());
  const [kind, setKind] = useState("all"); // all | year | month | day | range
  const [value, setValue] = useState({ year: today.slice(0, 4), month: today.slice(0, 7), day: today, from: today, to: today });
  const [from, to] = kind === "year" ? [`${value.year}-01-01`, `${value.year}-12-31`]
    : kind === "month" ? monthBounds(Number(value.month.slice(0, 4)), Number(value.month.slice(5, 7)) - 1)
    : kind === "day" ? [value.day, value.day]
    : kind === "range" ? [value.from <= value.to ? value.from : value.to, value.from <= value.to ? value.to : value.from]
    : ["", ""];
  // the charts follow the period: months for all dates and a year, days for a month, a day, or a short enough range
  const spanDays = kind === "range" ? (new Date(to) - new Date(from)) / 86_400_000 : null;
  const group = kind === "month" || kind === "day" || (kind === "range" && spanDays <= 62) ? "day" : "month";
  const [data, setData] = useState(null);
  const run = useRef(0);

  const load = () => {
    const n = ++run.current;
    api.get("/dashboard/production", { params: { date_from: from || undefined, date_to: to || undefined, group } })
      .then(({ data: d }) => n === run.current && setData(d))
      .catch(() => n === run.current && setData((d) => d || { error: true }));
  };
  // Jobs per department per month, counted the way the client's production logs count them (the whole financial year when no period is picked)
  const [dm, setDm] = useState(null);
  const loadDm = () => api.get("/dashboard/department-months", { params: { date_from: from || fyStart(), date_to: to || undefined, group: "month" } })
    .then(({ data: d }) => setDm(d)).catch(() => setDm((d) => d));
  useEffect(() => { load(); loadDm(); }, [kind, value.year, value.month, value.day, value.from, value.to]); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("jobs", () => { load(); loadDm(); });

  const label = kind === "all" ? "All dates" : kind === "year" ? value.year : kind === "month" ? `${MONTHS[Number(value.month.slice(5, 7)) - 1]} ${value.month.slice(0, 4)}`
    : kind === "range" ? `${formatDate(from)} – ${formatDate(to)}` : formatDate(value.day);
  const t = data?.totals, a = data?.averages;
  // "All dates" with nothing picked can carry a handful of stray months from a mistyped date somewhere in the
  // sheets (a "2026" entered as "0206", for instance) — those show as a lone "1" far off in 2005 or 2023 and
  // force every other month's label to crowd together. Not hidden, just not let them stretch the whole axis:
  // a month only counts as real activity here once it clears a small floor.
  const rawSeries = data?.series || [];
  const firstReal = rawSeries.findIndex((s) => s.jobs + s.received >= 10);
  const series = (kind === "all" && firstReal > 0 ? rawSeries.slice(firstReal) : rawSeries)
    // "Printed" splits into backlog cleared (received in an earlier period) and printed fresh (the rest),
    // stacked in the same bar so the total still reads as "jobs printed", with the carryover visible inside it.
    .map((s) => ({ ...s, printed_fresh: s.jobs - (s.backlog_cleared || 0), total_m: s.good_m + s.waste_m }));
  const trimmedStray = series.length < rawSeries.length;

  return (
    <section className="space-y-4" data-testid="production-analytics">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="label-caps">Production · {label}</div>
        <div className="text-xs text-neutral-500">From each job's Printing record (Printing stage, or the Production sheet import) and Quick Entry runs. Averages are per day, shift and run hour with printing.</div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="prod-date-filter">
        {[["all", "All"], ["year", "Year"], ["month", "Month"], ["day", "Day"], ["range", "Date range"]].map(([k, name]) => (
          <Button key={k} size="sm" variant={kind === k ? "default" : "outline"} onClick={() => setKind(k)}
            className={`rounded-sm h-7 px-3 text-xs ${kind === k ? "bg-[#FF5A00] hover:bg-[#e04f00]" : ""}`} data-testid={`prod-kind-${k}`}>{name}</Button>
        ))}
        {kind === "year" && (
          <select value={value.year} onChange={(e) => setValue({ ...value, year: e.target.value })} data-testid="prod-year"
            className="border border-neutral-300 rounded-sm h-7 px-2 text-xs bg-white">
            {yearsBack().map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        {kind === "month" && <Input type="month" value={value.month} onChange={(e) => e.target.value && setValue({ ...value, month: e.target.value })} data-testid="prod-month" className="rounded-sm w-40 h-7 text-xs" />}
        {kind === "day" && <Input type="date" value={value.day} onChange={(e) => e.target.value && setValue({ ...value, day: e.target.value })} data-testid="prod-day" className="rounded-sm w-40 h-7 text-xs" />}
        {kind === "range" && (
          <div className="flex items-center gap-1.5">
            <Input type="date" value={value.from} onChange={(e) => e.target.value && setValue({ ...value, from: e.target.value })} data-testid="prod-range-from" className="rounded-sm w-36 h-7 text-xs" />
            <span className="text-xs text-neutral-400">to</span>
            <Input type="date" value={value.to} onChange={(e) => e.target.value && setValue({ ...value, to: e.target.value })} data-testid="prod-range-to" className="rounded-sm w-36 h-7 text-xs" />
          </div>
        )}
      </div>

      <DeptMonths data={dm} />

      {data?.error && <div className="text-sm text-neutral-500">Production figures are not available.</div>}

      {data && !data.error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Kpi label="Jobs printed" value={t.jobs.toLocaleString("en-IN")} sub={`on ${t.working_days.toLocaleString("en-IN")} days · ${t.shifts.toLocaleString("en-IN")} shifts`} help={HELP.jobs} testid="kpi-prod-jobs" />
            <Kpi label="Jobs printed / day" value={num(a.jobs_per_day)} sub="per day with printing" help={HELP.jobsDay} testid="kpi-prod-jobs-day" />
            <Kpi label="Jobs printed / shift" value={num(a.jobs_per_shift)} sub="per shift with printing" help={HELP.jobsShift} testid="kpi-prod-jobs-shift" />
            <Kpi label="Good running metres" value={num(t.good_m)} sub={`in ${hrs(t.hours)}`} help={HELP.good} testid="kpi-prod-metres" />
            <Kpi label="Raw material used" value={pct(data.rm_consumption_pct)} sub={`${num(t.consumed_m)} m used of ${num(t.issued_m)} m issued`} help={HELP.rm} testid="kpi-prod-rm" />
            <Kpi label="Good output %" value={pct(data.efficiency.efficiency)} sub={`wastage ${pct(data.waste_pct)} of metres run`} help={HELP.efficiency} testid="kpi-prod-efficiency" />
            <Kpi label="Good running m / day" value={num(a.good_m.day)} help={HELP.goodAvg} testid="kpi-prod-m-day" />
            <Kpi label="Good running m / shift" value={num(a.good_m.shift)} help={HELP.goodAvg} testid="kpi-prod-m-shift" />
            <Kpi label="Good running m / run hour" value={num(a.good_m.hour)} help={HELP.goodAvg} testid="kpi-prod-m-hour" />
            <Kpi label="Wastage m / day" value={num(a.waste_m.day)} accent="text-rose-700" help={HELP.waste} testid="kpi-prod-w-day" />
            <Kpi label="Wastage m / shift" value={num(a.waste_m.shift)} accent="text-rose-700" help={HELP.waste} testid="kpi-prod-w-shift" />
            <Kpi label="Wastage m / run hour" value={num(a.waste_m.hour)} accent="text-rose-700" help={HELP.waste} testid="kpi-prod-w-hour" />
          </div>
          <Meaning />

          {data.excluded?.count > 0 && <Excluded ex={data.excluded} isAdmin={isAdmin} onIncluded={load} />}

          {trimmedStray && (
            <div className="border border-neutral-200 bg-neutral-50 px-4 py-2.5 text-xs text-neutral-600" data-testid="prod-stray-note">
              A few stray months with only 1–2 jobs (likely a mistyped date on one or two records, e.g. a year entered as "0206" instead of "2026")
              are left off the chart below so the real months aren't squeezed together. They're still counted in the totals above.
            </div> 
            
          )}

          {t.jobs_from_sheet > 0 && (
            <div className="text-xs text-neutral-500" data-testid="prod-sheet-note">
              {t.jobs_from_sheet.toLocaleString("en-IN")} of these {t.jobs.toLocaleString("en-IN")} jobs have no run figures yet (no counter reading, or counted from the tracking sheet),
              so they are in the job counts only. Metres, wastage, operator, shift and machine figures come from the filled-in runs alone.
            </div>
          )}

          {t.jobs === 0 && t.sessions === 0 && (
            <div className="text-xs text-neutral-500" data-testid="prod-empty">
              No production recorded in this period yet. It fills in from the Printing sheets (date, times, operator, run counters) and the production log.
            </div>
          )}

          {(t.sessions > 0 || t.jobs > 0) && series.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Chart
                title={`Jobs Printed per ${group}`}
                testid="prod-chart-jobs"
                key2={
                  <div className="flex items-center gap-3 text-[10px] text-neutral-500">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 inline-block" style={{ background: ORANGE }} />Printed</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 inline-block" style={{ background: AMBER }} />of which, older jobs</span>
                    <span className="flex items-center gap-1"><span className="w-3 border-t-2 border-dashed inline-block" style={{ borderColor: SLATE }} />Got</span>
                  </div>
                }
              >
                {/* One bar per period (Printed, backlog cleared stacked inside it) with Got drawn as a line over
                    it — merged into a single clean chart instead of two bars fighting for width, and the line
                    makes the Got-vs-Printed gap (backlog still pending) easy to see at a glance. */}
                <ComposedChart data={series} margin={{ top: 22, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#eee" strokeDasharray="2 2" vertical={false} />
                  <XAxis dataKey="key" fontSize={11} stroke="#71717A" tickFormatter={(k) => tick(k, group)} interval={series.length > 20 ? "preserveStartEnd" : 0} />
                  <YAxis fontSize={11} stroke="#71717A" allowDecimals={false} />
                  <Tooltip cursor={{ fill: "#F4F4F5" }} contentStyle={TOOLTIP_STYLE} labelFormatter={(k) => (group === "day" ? formatDate(k) : k)}
                    formatter={(v, name) => [v, name === "received" ? "Got" : name === "backlog_cleared" ? "— received earlier, printed this period" : "Printed (fresh)"]} />
                  <Bar dataKey="backlog_cleared" stackId="printed" fill={AMBER} name="backlog_cleared">
                    {series.length <= 20 && <LabelList dataKey="backlog_cleared" content={insideLabel(series, "backlog_cleared", "older jobs")} />}
                  </Bar>
                  <Bar dataKey="printed_fresh" stackId="printed" fill={ORANGE} name="printed_fresh">
                    {series.length <= 31 && <LabelList dataKey="jobs" content={valueLabel(series, "jobs", "#9A3412", 9)} />}
                  </Bar>
                  <Line type="monotone" dataKey="received" name="received" stroke={SLATE} strokeWidth={2} strokeDasharray="4 2" dot={{ r: 2, fill: SLATE }} />
                </ComposedChart>
              </Chart>
              {t.sessions > 0 && <Chart title={`Good running metres and wastage per ${group}`} testid="prod-chart-metres"
                key2={<span className="inline-flex items-center gap-3 text-[11px] text-neutral-500">
                  <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 inline-block" style={{ background: ORANGE }} />good</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 inline-block" style={{ background: ROSE }} />wastage</span></span>}>
                {/* Good + wastage are the same metres run, split two ways — stacked into one bar per period so
                    the full height reads as the total run, same treatment as the Jobs Printed chart. */}
                <BarChart data={series} margin={{ top: 22, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#eee" strokeDasharray="2 2" vertical={false} />
                  <XAxis dataKey="key" fontSize={11} stroke="#71717A" tickFormatter={(k) => tick(k, group)} interval={series.length > 20 ? "preserveStartEnd" : 0} />
                  <YAxis fontSize={11} stroke="#71717A" tickFormatter={(v) => formatShort(v, 1)} />
                  <Tooltip cursor={{ fill: "#F4F4F5" }} contentStyle={TOOLTIP_STYLE} labelFormatter={(k) => (group === "day" ? formatDate(k) : k)}
                    formatter={(v, name) => [`${Number(v).toLocaleString("en-IN")} m`, name === "good_m" ? "Good" : "Wastage"]} />
                  <Bar dataKey="good_m" stackId="metres" fill={ORANGE}>
                    {series.length <= 20 && <LabelList dataKey="waste_m" content={insideLabel(series, "waste_m", "waste", 34, (v) => formatShort(v, 1))} />}
                  </Bar>
                  <Bar dataKey="waste_m" stackId="metres" fill={ROSE}>
                    {series.length <= 16 && <LabelList dataKey="total_m" content={valueLabel(series, "total_m", "#9A3412", 9)} />}
                  </Bar>
                </BarChart>
              </Chart>}
            </div>
          )}

          {t.sessions > 0 && (<>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="space-y-4">
              <Table title="Shift-wise" testid="prod-shifts" rows={data.shifts} empty="No shifts in this period."
                cols={[["Shift", (r) => r.name], ["Jobs", (r) => r.jobs], ["Hours", (r) => hrs(r.hours)], ["Good m", (r) => metres(r.good_m)], ["Waste m", (r) => metres(r.waste_m)]]} />
              {(data.departments || []).length > 0 && (
                <Table title="Department-wise" testid="prod-departments" rows={data.departments} empty=""
                  cols={[["Department", (r) => r.name], ["Jobs", (r) => r.jobs], ["Hours", (r) => hrs(r.hours)], ["Good m", (r) => metres(r.good_m)], ["Efficiency", (r) => <Eff v={r.efficiency} />]]} />
              )}
            </div>
            <div className="xl:col-span-2">
              <Table title="Operator-wise jobs" testid="prod-operators" rows={data.operators} empty="No operator data in this period." maxBodyHeight="28rem"
                cols={[["Operator", (r) => r.name], ["Dept", (r) => r.department || "—"], ["Jobs", (r) => r.jobs], ["Runs", (r) => r.sessions], ["Hours", (r) => hrs(r.hours)], ["Good m", (r) => metres(r.good_m)],
                  ["Waste m", (r) => metres(r.waste_m)], ["Waste %", (r) => pct(r.waste_pct)], ["m / hour", (r) => metres(r.m_per_hour)], ["Efficiency", (r) => <Eff v={r.efficiency} />]]} />
            </div>
          </div>
          <Table title="Machine efficiency" testid="prod-machines" rows={data.machines} empty="No machine data in this period."
            cols={[["Machine", (r) => r.name], ["Jobs", (r) => r.jobs], ["Hours", (r) => hrs(r.hours)], ["Good m", (r) => metres(r.good_m)], ["Waste m", (r) => metres(r.waste_m)],
              ["Availability", (r) => pct(r.availability)], ["Performance", (r) => pct(r.performance)], ["Quality", (r) => pct(r.quality)], ["Efficiency", (r) => <Eff v={r.efficiency} />]]} />
          <div className="text-[11px] text-neutral-500">
            Efficiency = availability (run time against run + down time) × performance (output against the machine's rated capacity, when set) × quality (good against good + wasted).
            Parts with no data count as 100%. Machine is the Equipment I.D. on the Printing sheet, or the machine of a production log.
          </div>
          </>)}
        </>
      )}
    </section>
  );
}

/** Jobs per department per month: Printing, AVT and Slitting, as the client's own production logs count them. */
function DeptMonths({ data }) {
  if (!data || !data.periods?.length) return null;
  const mon = (k) => `${MONTHS[Number(k.slice(5, 7)) - 1]} ${k.slice(0, 4)}`;
  const begins = data.departments.filter((d) => d.logs_from).map((d) => `${d.label} ${formatDate(d.logs_from)}`);
  return (
    <div className="border border-neutral-200 bg-white" data-testid="prod-dept-months">
      <div className="px-4 py-2.5 border-b border-neutral-200 label-caps">Jobs per department · by month</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 label-caps text-[10px]">
            <tr>
              <th className="px-3 py-2 text-left">Month</th>
              {data.departments.map((d) => <th key={d.key} className="px-3 py-2 text-right">{d.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.periods.map((k) => (
              <tr key={k} className="border-t border-neutral-100">
                <td className="px-3 py-2 font-medium whitespace-nowrap">{mon(k)}</td>
                {data.departments.map((d) => (
                  <td key={d.key} className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`dept-${d.key}-${k}`}>{(d.counts[k] || 0).toLocaleString("en-IN")}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11px] text-neutral-500" data-testid="prod-dept-note">
        A job counts in every month it had a run in the production logs. {begins.length
          ? `Before the logs begin (${begins.join(" · ")}) it counts on its date in the Medpack tracking sheet.`
          : "No production logs have been imported yet: jobs count on their date in the Medpack tracking sheet."}
      </div>
    </div>
  );
}

// A record left out for an impossible value (not a missing date) can be reviewed by an
// admin and counted anyway — one job at a time, so it's a deliberate call each time, not
// a blanket switch that would quietly let bad data back into the figures.
const FIXABLE_REASON = "partial counter above total counter";

// Printing records whose figures cannot be right: counted as jobs, left out of metres, wastage and efficiency.
function Excluded({ ex, isAdmin, onIncluded }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const include = (r) => {
    setBusy(r.job_id);
    api.post("/dashboard/production/override", { job_id: r.job_id, kind: "printing" })
      .then(() => { toast.success(`${r.bpr} now counted in the figures`); onIncluded?.(); })
      .catch(() => toast.error("Could not include this record"))
      .finally(() => setBusy(null));
  };
  return (
    <div className="border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900" data-testid="prod-excluded">
      <div className="flex flex-wrap items-center gap-2">
        <span>
          <b>{ex.count.toLocaleString("en-IN")}</b> printing record{ex.count === 1 ? "" : "s"} left out of the figures: either the partial counter
          is above the total counter (an impossible value; these still count as jobs) or the record has no printing date.
        </span>
        <button type="button" onClick={() => setOpen(!open)} className="underline" data-testid="prod-excluded-toggle">{open ? "Hide" : "Show which"}</button>
      </div>
      {open && (
        <div className="mt-2 max-h-56 overflow-auto bg-white border border-amber-200" data-testid="prod-excluded-list">
          <table className="w-full text-[11px]">
            <tbody>
              {ex.rows.map((r) => (
                <tr key={`${r.job_id}-${r.date}`} className="border-t border-amber-100">
                  <td className="px-2 py-1 font-mono-num"><Link to={`/orders/${r.job_id}?tab=production_flow&stage=printing`} className="underline">{r.bpr}</Link></td>
                  <td className="px-2 py-1 font-mono-num">{r.date}</td>
                  <td className="px-2 py-1">{r.reason}</td>
                  <td className="px-2 py-1 text-right">
                    {isAdmin && r.reason === FIXABLE_REASON && (
                      <button type="button" onClick={() => include(r)} disabled={busy === r.job_id}
                        className="underline text-emerald-700 disabled:opacity-50" data-testid={`prod-excluded-add-${r.job_id}`}>
                        {busy === r.job_id ? "Adding…" : "Add anyway"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {ex.count > ex.rows.length && <div className="px-2 py-1 text-neutral-500">Latest {ex.rows.length} shown of {ex.count.toLocaleString("en-IN")}.</div>}
        </div>
      )}
    </div>
  );
}

function Eff({ v }) {
  if (v == null) return <span className="text-neutral-400">—</span>;
  const tone = v >= 85 ? "bg-emerald-100 text-emerald-800" : v >= 60 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800";
  return <span className={`inline-block px-1.5 py-0.5 rounded-sm font-mono-num font-semibold ${tone}`}>{v}%</span>;
}

function Chart({ title, children, testid, key2, sub }) {
  return (
    <div className="border border-neutral-200 bg-white p-5" data-testid={testid}>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-4">
        <div className="label-caps">{title}</div>
        {key2}
      </div>
      <div className="h-56"><ResponsiveContainer>{children}</ResponsiveContainer></div>
      {sub && <div className="mt-2 text-[11px] text-neutral-400">{sub}</div>}
    </div>
  );
}

function Table({ title, rows, cols, empty, testid, maxBodyHeight }) {
  return (
    <div className="border border-neutral-200 bg-white" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-neutral-200 label-caps">{title}</div>
      <div className={`overflow-x-auto ${maxBodyHeight ? "overflow-y-auto" : ""}`} style={maxBodyHeight ? { maxHeight: maxBodyHeight } : undefined}>
        <table className="w-full text-sm">
          <thead className={`bg-neutral-50 label-caps text-[10px] ${maxBodyHeight ? "sticky top-0 z-10" : ""}`}>
            <tr>{cols.map(([h], i) => <th key={h} className={`px-3 py-2 ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-neutral-100">
                {cols.map(([h, f], i) => <td key={h} className={`px-3 py-2 ${i === 0 ? "text-left font-medium" : "text-right font-mono-num text-xs"}`}>{f(r)}</td>)}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-6 text-center text-neutral-400 text-sm">{empty}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent, help, testid }) {
  return (
    <div className="border border-neutral-200 bg-white px-4 py-3" data-testid={testid} title={help}>
      <div className="label-caps text-[10px] leading-tight flex items-start gap-1">
        <span>{label}</span>
        {help && <Info size={11} className="shrink-0 text-neutral-400 mt-px" aria-label={help} />}
      </div>
      <div className={`font-mono-num text-lg font-bold mt-1.5 tracking-tight ${accent || "text-neutral-900"}`}>{value}</div>
      {sub && <div className="text-[11px] text-neutral-500 font-mono-num mt-0.5 leading-snug">{sub}</div>}
    </div>
  );
}

// The same explanations as a list, for phones (no hover) and for people new to the dashboard.
function Meaning() {
  const [open, setOpen] = useState(false);
  const rows = [
    ["Jobs printed", HELP.jobs], ["Jobs printed / day, / shift", "Jobs printed divided by the days, or by the shifts, that had printing. Shifts: A 06-14, B 14-22, C 22-06, from the start time."],
    ["Good running metres", HELP.good], ["Raw material used", HELP.rm], ["Good output %", HELP.efficiency],
    ["Good running m / day, shift, run hour", HELP.goodAvg], ["Wastage m / day, shift, run hour", HELP.waste],
  ];
  return (
    <div className="text-xs" data-testid="prod-meaning">
      <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-neutral-500 hover:text-neutral-800 underline decoration-dotted" data-testid="prod-meaning-toggle">
        <Info size={12} /> {open ? "Hide what these figures mean" : "What these figures mean"}
      </button>
      {open && (
        <dl className="mt-2 border border-neutral-200 bg-white divide-y divide-neutral-100" data-testid="prod-meaning-list">
          {rows.map(([k, v]) => (
            <div key={k} className="px-4 py-2 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-1 md:gap-4">
              <dt className="font-medium text-neutral-800">{k}</dt>
              <dd className="text-neutral-600">{v}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
